/* eslint-disable @typescript-eslint/no-explicit-any */
// Honestly "stolen" from mediasoup-client
import { EventEmitter } from 'events-alias';

type Events = Record<PropertyKey, any[]>;

/**
 * Типизированный EventEmitter, который используется во внешнем API SDK для подписки на события.
 *
 * Позволяет получать подсказки по именам событий и их аргументам в приложении,
 * не привязываясь к внутренним деталям реализации.
 */
export default class EnhancedEventEmitter<
  E extends Record<keyof E, any[]> = Events,
> extends EventEmitter {
  constructor() {
    super();

    this.setMaxListeners(Infinity);
  }

  override emit<K extends keyof E & string>(eventName: K, ...args: E[K]): boolean {
    return super.emit(eventName, ...args);
  }

  safeEmit<K extends keyof E & string>(eventName: K, ...args: E[K]): boolean {
    try {
      return super.emit(eventName, ...args);
    } catch (err) {
      try {
        super.emit('listenererror', eventName, err);
      } catch {
        // do nothing if listenererror fails
      }

      return !!super.listenerCount(eventName);
    }
  }

  override on<K extends keyof E & string>(
    eventName: K,
    listener: (...args: E[K]) => void,
  ): this {
    super.on(eventName, listener as (...args: any[]) => void);

    return this;
  }

  override off<K extends keyof E & string>(
    eventName: K,
    listener: (...args: E[K]) => void,
  ): this {
    super.off(eventName, listener as (...args: any[]) => void);

    return this;
  }

  override addListener<K extends keyof E & string>(
    eventName: K,
    listener: (...args: E[K]) => void,
  ): this {
    super.on(eventName, listener as (...args: any[]) => void);

    return this;
  }

  override removeListener<K extends keyof E & string>(
    eventName: K,
    listener: (...args: E[K]) => void,
  ): this {
    super.off(eventName, listener as (...args: any[]) => void);

    return this;
  }

  override once<K extends keyof E & string>(
    eventName: K,
    listener: (...args: E[K]) => void,
  ): this {
    super.once(eventName, listener as (...args: any[]) => void);

    return this;
  }

  override prependListener<K extends keyof E & string>(
    eventName: K,
    listener: (...args: E[K]) => void,
  ): this {
    super.prependListener(eventName, listener as (...args: any[]) => void);

    return this;
  }

  override prependOnceListener<K extends keyof E & string>(
    eventName: K,
    listener: (...args: E[K]) => void,
  ): this {
    super.prependOnceListener(eventName, listener as (...args: any[]) => void);

    return this;
  }

  override removeAllListeners(eventName?: keyof E & string): this {
    super.removeAllListeners(eventName);

    return this;
  }
}
